import { useCallback, useEffect } from 'react';
import { UseNavigateResult } from '@tanstack/react-router';
import { Authn, HandleCallbackProps } from '@frontend/auth';
import { SignInMethod } from '@frontend/auth-helpers';
import appConfig from '@frontend/env';
import { useTranslation } from '@frontend/i18n';
import { hydraShellService } from '../services';
import { getRedirectURL } from '../services/fetch-wrapper.ts';
import { handlePostAuthRedirect } from '../services/route-utils';
import { ContentLoader } from './content-loader.tsx';
import { FormWrap } from './form-wrap';

interface Props<T extends string> {
  auth: Authn;
  navigate: UseNavigateResult<T>;
  signInMethod: SignInMethod;
  getAuthCallbackProps?: () => HandleCallbackProps | undefined;
}

export const SignInCallback = <T extends string>({ auth, navigate, signInMethod, getAuthCallbackProps }: Props<T>) => {
  const { t } = useTranslation('SignIn');

  const handleCallback = useCallback(async () => {
    let redirectURL = appConfig.DEFAULT_REDIRECT;
    try {
      const initialLoginResponse = await hydraShellService.initializeOAuth2Flow();
      redirectURL = !!initialLoginResponse.requestUrl
        ? getRedirectURL(initialLoginResponse.requestUrl) ?? appConfig.DEFAULT_REDIRECT
        : appConfig.DEFAULT_REDIRECT;
    } catch (error) {
      console.error(error);
    }

    if (!auth.isUserAuthenticated()) {
      auth.changeAuthMethod(signInMethod);

      let callbackProps: HandleCallbackProps | undefined = undefined;
      if (getAuthCallbackProps) {
        callbackProps = getAuthCallbackProps();
      }

      try {
        await auth.handleCallback(callbackProps);
        const acceptLoginResponse = await hydraShellService.acceptOAuth2Flow();
        handlePostAuthRedirect(acceptLoginResponse.redirectTo || appConfig.DEFAULT_REDIRECT, navigate, {
          reason: `acceptLoginResponse for ${signInMethod}`,
        });
      } catch (e: any) {
        console.error(e);
        handlePostAuthRedirect(redirectURL || appConfig.DEFAULT_REDIRECT, navigate, {
          reason: `error caught in sign-in callback ${signInMethod}`,
          error: e,
        });
      }
    } else {
      navigate({ to: redirectURL || appConfig.DEFAULT_REDIRECT });
    }
  }, [auth, getAuthCallbackProps, navigate, signInMethod]);

  useEffect(() => {
    handleCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormWrap>
      <ContentLoader message={t('Logging in...')} show />
    </FormWrap>
  );
};
